import React, { useState, useEffect, useRef } from "react";

const CountdownOverlay = ({
  width = window.innerWidth,
  height = window.innerHeight,
  onCountdownComplete,
  delay = 4000,
  startFrom = 3,
}) => {
  const [count, setCount] = useState(null); // null = hidden
  const [visible, setVisible] = useState(false);
  const audioContextRef = useRef(null);
  const tickTimerRef = useRef(null);
  const delayTimerRef = useRef(null);

  // beep sound (may be blocked until user interacts with page)
  const playBeep = () => {
    try {
      if (!audioContextRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new Ctx();
      }
      const ctx = audioContextRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 800;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.18);
    } catch (e) {
      // silently ignore audio errors (some browsers block autoplay)
    }
  };

  // Start overlay after `delay`
  useEffect(() => {
    delayTimerRef.current = setTimeout(() => {
      setVisible(true);
      setCount(startFrom);
    }, delay);

    return () => {
      clearTimeout(delayTimerRef.current);
      clearTimeout(tickTimerRef.current);
    };
  }, [delay, startFrom]);

  // countdown tick
  useEffect(() => {
    if (!visible || count === null) return;

    if (count > 0) {
      // schedule next tick
      tickTimerRef.current = setTimeout(() => {
        setCount((c) => c - 1);
      }, 1000);
    } else {
      // finished
      setVisible(false);
      // make sure timer is cleared
      clearTimeout(tickTimerRef.current);
      if (typeof onCountdownComplete === "function") {
        onCountdownComplete();
      }
      // also dispatch event if other parts of the app expect it
      try {
        window.dispatchEvent(new CustomEvent("countdown-complete"));
      } catch (e) {}
    }

    return () => clearTimeout(tickTimerRef.current);
  }, [visible, count, onCountdownComplete]);

  // play beep when showing 3,2,1
  useEffect(() => {
    if (count !== null && count > 0) playBeep();
  }, [count]);

  if (!visible || count === null) return null;

  const numbers = [3, 2, 1];

  const getStatus = (n) => {
    // countdown decreases: n > count => completed, n === count => current, n < count => upcoming
    if (n === count) return "current";
    if (n > count) return "completed";
    return "upcoming";
  };

  const boxStyleBase = {
    width: 150,
    height: 150,
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 10px",
    transition:
      "transform 160ms ease, opacity 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
    boxSizing: "border-box",
    userSelect: "none",
  };

  const renderBox = (n) => {
    const status = getStatus(n);

    let style = { ...boxStyleBase };
    let numberStyle = {
      color: "white",
      fontWeight: 700,
      lineHeight: 1,
      transition: "font-size 160ms ease, opacity 160ms ease",
    };

    if (status === "current") {
      style.background = "rgba(0,0,0,0.92)";
      // style.border = "2px solid rgba(255,255,255,0.95)";
      // style.transform = "scale(1.06)";
      style.boxShadow = "0 6px 18px rgba(0,0,0,0.45)";
      numberStyle.fontSize = 50;
      numberStyle.opacity = 1;
    } else if (status === "completed") {
      // Completed is highlighted (but slightly smaller than current)
      style.background = "rgba(0,0,0,0.85)";
      // style.border = "1px solid rgba(255,255,255,0.85)";
      // style.transform = "scale(1)";
      style.boxShadow = "none";
      numberStyle.fontSize = 50;
      numberStyle.opacity = 0.95;
    } else {
      // Upcoming / not yet completed -> disabled-dimmed
      style.background = "rgba(0,0,0,0.25)";
      style.border = "1px solid rgba(255,255,255,0.08)";
      // style.transform = "scale(0.98)";
      style.filter = "grayscale(20%)";
      // make it appear disabled
      style.opacity = 0.45;
      numberStyle.fontSize = 50;
      numberStyle.opacity = 0.6;
    }

    return (
      <div
        key={n}
        aria-hidden={status === "upcoming"} // mark upcoming as not important
        style={style}
      >
        <span style={numberStyle}>{n}</span>
      </div>
    );
  };

  // Overlay wrapper is centered on the screen
  const wrapperStyle = {
    position: "absolute",
    inset: 0, // fill parent container
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    pointerEvents: "none",
  };
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
  };

  return (
    <div style={wrapperStyle} role="status" aria-live="polite">
      <div style={rowStyle}>{numbers.map((n) => renderBox(n))}</div>
    </div>
  );
};

export default CountdownOverlay;
